import { createRouter, createWebHistory } from "vue-router";
import { isAdmin, isAdminConsoleUser } from "@/utils/adminRole";

const guardMyroute = (to, from, next) => {
  var isAuthenticated = false;
  //this is just an example. You will have to find a better or
  // centralised way to handle you localstorage data handling
  if (localStorage.getItem("token")) isAuthenticated = true;
  else isAuthenticated = false;
  if (isAuthenticated) {
    next(); // allow to enter route
  } else if (to.fullPath && to.fullPath !== "/") {
    // Keep the destination (e.g. an emailed offer link) for after login.
    next({ path: "/login", query: { redirect: to.fullPath } });
  } else {
    next("/login"); // go to '/login';
  }
};
const checkIfLoggedIn = (to, from, next) => { 
  var isAuthenticated = false;
  //this is just an example. You will have to find a better or
  // centralised way to handle you localstorage data handling
  if (localStorage.getItem("token")) isAuthenticated = true;
  else isAuthenticated = false;
  if (isAuthenticated) {
    next("/"); // allow to enter route
  } else {
    next();
  }
};

// Admins and sales staff (read-only) share the admin console
const guardAdmin = (to, from, next) => {
  if (!localStorage.getItem("token")) {
    next("/admin/login");
    return;
  }
  if (!isAdminConsoleUser()) {
    next("/");
    return;
  }
  // Pages that exist only to change things (CSV import, payment settings,
  // sales team) are for admins alone
  if (to.meta.adminOnly && !isAdmin()) {
    next("/admin");
    return;
  }
  next();
};

const checkIfAdminLoggedIn = (to, from, next) => {
  if (localStorage.getItem("token") && isAdminConsoleUser()) {
    next("/admin");
    return;
  }
  next();
};

const routes = [
  {
    path: "/",
    name: "Home",
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "shop" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/Home.vue"
      ),
  },
  // New passwordless sign-up + login flow. Not theme-scoped: it has one
  // design. `authMode` picks which side of the switch the page opens on.
  {
    path: "/login",
    name: "Login",
    beforeEnter: checkIfLoggedIn,
    meta: {
      requiresLogin: false,
      authMode: "login",
    },
    component: () =>
      import(
        /* webpackChunkName: "onboarding" */ "../views/onboarding/SignUpFlow.vue"
      ),
  },
  {
    path: "/register",
    name: "Register",
    beforeEnter: checkIfLoggedIn,
    meta: {
      requiresLogin: false,
      authMode: "signup",
    },
    component: () =>
      import(
        /* webpackChunkName: "onboarding" */ "../views/onboarding/SignUpFlow.vue"
      ),
  },
  {
    // Public: linked from the sign-up flow, so it has to render before an
    // account exists.
    path: "/terms",
    name: "TermsOfService",
    meta: {
      requiresLogin: false,
    },
    component: () =>
      import(
        /* webpackChunkName: "legal" */ "../views/legal/TermsOfService.vue"
      ),
  },
  {
    // Public accessibility statement (A11Y-01), linked from the site footer.
    path: "/accessibility",
    name: "Accessibility",
    meta: {
      requiresLogin: false,
    },
    component: () =>
      import(
        /* webpackChunkName: "legal" */ "../views/legal/Accessibility.vue"
      ),
  },
  {
    // Kept because emailed verification links point here.
    path: "/get-started",
    name: "SignUpFlow",
    beforeEnter: checkIfLoggedIn,
    meta: {
      requiresLogin: false,
      authMode: "signup",
    },
    component: () =>
      import(
        /* webpackChunkName: "onboarding" */ "../views/onboarding/SignUpFlow.vue"
      ),
  },
  {
    // Previous email/password screens, kept reachable while the new flow beds in.
    path: "/login-v1",
    name: "LoginV1",
    beforeEnter: checkIfLoggedIn,
    meta: {
      requiresLogin: false,
    },
    component: () =>
      import(
        /* webpackChunkName: "shop" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/Login.vue"
      ),
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    beforeEnter: checkIfLoggedIn,
    meta: {
      requiresLogin: false,
    },
    component: () =>
      import(
        /* webpackChunkName: "auth" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/ForgotPassword.vue"
      ),
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    beforeEnter: checkIfLoggedIn,
    meta: {
      requiresLogin: false,
    },
    component: () =>
      import(
        /* webpackChunkName: "auth" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/ResetPassword.vue"
      ),
  },
  {
    path: "/register-old",
    name: "Register-old",
    beforeEnter: checkIfLoggedIn,
    meta: {
      requiresLogin: false,
    },
    component: () =>
      import(
        /* webpackChunkName: "shop" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/Register-old.vue"
      ),
  },
  {
    // Previous multi-step email/password registration, moved aside.
    path: "/register-v1",
    name: "RegisterV1",
    beforeEnter: checkIfLoggedIn,
    meta: {
      requiresLogin: false,
    },
    component: () =>
      import(
        /* webpackChunkName: "shop" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/Register.vue"
      ),
  },
  {
    // Post-registration confirmation page used as the Meta Ads conversion
    // landing page. Must remain public (registration does not auto-login —
    // users land here, then verify email, then sign in).
    // Slug intentionally distinct from the marketing site's /thank-you
    // (contact-form confirmation) so the two conversions can be tracked
    // and optimized independently.
    path: "/registration-thank-you",
    name: "ThankYou",
    meta: {
      requiresLogin: false,
    },
    component: () =>
      import(
        /* webpackChunkName: "thank-you" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/ThankYou.vue"
      ),
  },
  {
    path: "/shop/:id",
    name: "ShopDefault",
    // route level code-splitting
    // this generates a separate chunk (shop.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "shop" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/Shop.vue"
      ),
  },
  {
    path: "/home/:id/model",
    name: "SeriesDefault",
    // route level code-splitting
    // this generates a separate chunk (shop.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "shop" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/Series.vue"
      ),
  },
  // Backward compatibility: redirect old series route
  { path: "/series/:id", redirect: to => ({ path: `/home/${to.params.id}/model` }) },
  {
    path: "/bid/:id",
    name: "BidDetails",
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "bid-details" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/BidDetails.vue"
      ),
  },
  {
    path: "/shop/:pageNo",
    name: "ShopWithPagination",
    // route level code-splitting
    // this generates a separate chunk (shop.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "shop" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/Shop.vue"
      ),
  },
  {
    path: "/cart",
    name: "Cart",
    // route level code-splitting
    // this generates a separate chunk (shop.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "cart" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/Cart.vue"
      ),
  },

  {
    path: "/products/:id",
    name: "Product",
    // route level code-splitting
    // this generates a separate chunk (shop.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "product" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/Product.vue"
      ),
  },
  {
    path: "/checkout",
    name: "Checkout",
    // route level code-splitting
    // this generates a separate chunk (shop.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "checkout" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/Checkout.vue"
      ),
  },
  {
    path: "/blog-list",
    name: "BlogList",
    // route level code-splitting
    // this generates a separate chunk (shop.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "blog-list" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/BlogList.vue"
      ),
  },
  {
    path: process.env.VUE_APP_BLOG_PATH + ":blogID",
    name: "BlogPost",
    // route level code-splitting
    // this generates a separate chunk (shop.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "blogpost" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/BlogPost.vue"
      ),
  },
  {
    path: "/about",
    name: "About",
    // route level code-splitting
    // this generates a separate chunk (shop.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "about" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/About.vue"
      ),
  },
  {
    path: "/my-account",
    name: "MyAccount",
    // route level code-splitting
    // this generates a separate chunk (shop.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "myaccount" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/MyAccount.vue"
      ),
  },
  {
    path: "/wishlist",
    name: "Wishlist",
    // route level code-splitting
    // this generates a separate chunk (shop.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "wishlist" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/Wishlist.vue"
      ),
  },
  {
    path: "/contact",
    name: "Contact",
    // route level code-splitting
    // this generates a separate chunk (shop.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "contact" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/Contact.vue"
      ),
  },
  {
    // Logged-in only. Guest / third-party entry is /build-your-car.
    // The picked make/model/trim are mirrored into the path as slugs
    // (/quick-bid/audi/a5/all-new-a5) so a build can be linked to directly.
    path: "/quick-bid/:make?/:model?/:trim?",
    name: "QuickBid",
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "quickbid" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/QuickBid.vue"
      ),
  },
  {
    // Public embed-style entry for third-party / guest users. Same wizard as
    // Quick Bid; auth is required only on submit (draft → register → resume).
    path: "/build-your-car/:make?/:model?/:trim?",
    name: "BuildYourCar",
    meta: { bareLayout: true, requiresLogin: false },
    // Logged-in users get the same wizard with site chrome on /quick-bid,
    // keeping slugs + query so deep links (and belowmsrp.com hand-offs) still
    // apply. This must redirect here, not in QuickBid.created(): both routes
    // use QuickBid.vue, so switching between them reuses the instance and
    // created() would not run again on /quick-bid.
    beforeEnter: (to) => {
      if (!localStorage.getItem("token")) return true;
      const slugs = [to.params.make, to.params.model, to.params.trim].filter(Boolean).join("/");
      return { path: slugs ? "/quick-bid/" + slugs : "/quick-bid", query: to.query, replace: true };
    },
    component: () =>
      import(
        /* webpackChunkName: "quickbid" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/QuickBid.vue"
      ),
  },
  {
    path: "/offers",
    name: "Offer",
    // route level code-splitting
    // this generates a separate chunk (shop.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "contact" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/offers.vue"
      ),
  },
  {
    path: "/offers/all-offer/:userBidId",
    name: "All-Offer",
    // route level code-splitting
    // this generates a separate chunk (shop.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "contact" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/AllOffers.vue"
      ),
  },
  {
    path: "/trade-in",
    name: "TradeIn",
    // route level code-splitting
    // this generates a separate chunk (shop.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "contact" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/TradeIn.vue"
      ),
  },
   {
    path: "/profile",
    name: "Profile",
    // route level code-splitting
    // this generates a separate chunk (shop.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "contact" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/Profile.vue"
      ),
  },
  {
    path: "/payment-calculator",
    name: "PaymentCalculator",
    // route level code-splitting
    // this generates a separate chunk (shop.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "payment-calculator" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/PaymentCalculator.vue"
      ),
  },
  {
    path: "/check-csv",
    name: "CheckCsvList",
    // public helper page to preview CSV car data
    meta: { requiresLogin: false },
    component: () =>
      import(
        /* webpackChunkName: "check-csv" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/CheckCsv.vue"
      ),
  },
  {
    path: "/check-csv/shop/:key",
    name: "CheckCsvShop",
    meta: { requiresLogin: false },
    component: () =>
      import(
        /* webpackChunkName: "check-csv-shop" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/CheckCsvShop.vue"
      ),
  },
  {
    path: "/check-csv/product/:key",
    name: "CheckCsvProduct",
    meta: { requiresLogin: false },
    component: () =>
      import(
        /* webpackChunkName: "check-csv-product" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/CheckCsvProduct.vue"
      ),
  },
  // Keep old route for backward compatibility or direct access, pointing to product
  {
    path: "/check-csv/:key",
    name: "CheckCsvDetail",
    redirect: to => `/check-csv/product/${to.params.key}`
  },
  // ---- Process CSV ----
  // The importer moved into the admin console (it rewrites the live catalog),
  // so the old public pages just forward there.
  { path: "/process-csv", redirect: "/admin/csv-import" },
  { path: "/process-csv/shop/:key", redirect: "/admin/csv-import" },
  { path: "/process-csv/product/:key", redirect: "/admin/csv-import" },
  {
    path: "/admin/login",
    name: "AdminLogin",
    beforeEnter: checkIfAdminLoggedIn,
    meta: { requiresLogin: false },
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/AdminLogin.vue"
      ),
  },
  {
    path: "/admin",
    name: "Admin",
    beforeEnter: guardAdmin,
    meta: { requiresAdmin: true },
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/Admin.vue"
      ),
  },
  {
    path: "/admin/dealers/:id/bids/:negotiationId",
    name: "AdminDealerBidDetail",
    beforeEnter: guardAdmin,
    meta: { requiresAdmin: true },
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/AdminDealerBidDetail.vue"
      ),
  },
  {
    path: "/admin/dealers/:id",
    name: "AdminDealerDetail",
    beforeEnter: guardAdmin,
    meta: { requiresAdmin: true },
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/AdminDealerDetail.vue"
      ),
  },
  // Catalog walk-through: brands -> series -> models -> model detail. One view
  // renders every step; which step is showing comes from the route params.
  {
    path: "/admin/catalog",
    name: "AdminCatalog",
    beforeEnter: guardAdmin,
    meta: { requiresAdmin: true },
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/AdminCatalog.vue"
      ),
  },
  {
    path: "/admin/catalog/:companyId",
    name: "AdminCatalogSeries",
    beforeEnter: guardAdmin,
    meta: { requiresAdmin: true },
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/AdminCatalog.vue"
      ),
  },
  {
    path: "/admin/catalog/:companyId/series/:seriesId",
    name: "AdminCatalogModels",
    beforeEnter: guardAdmin,
    meta: { requiresAdmin: true },
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/AdminCatalog.vue"
      ),
  },
  {
    path: "/admin/catalog/:companyId/model/:carId",
    name: "AdminCatalogModelDetail",
    beforeEnter: guardAdmin,
    meta: { requiresAdmin: true },
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/AdminCatalog.vue"
      ),
  },
  {
    path: "/admin/csv-import",
    name: "AdminCsvImport",
    beforeEnter: guardAdmin,
    meta: { requiresAdmin: true, adminOnly: true },
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/AdminCsvImport.vue"
      ),
  },
  // Not in the admin navigation on purpose: reachable by direct link only.
  {
    path: "/admin/payment-settings",
    name: "AdminPaymentSettings",
    beforeEnter: guardAdmin,
    meta: { requiresAdmin: true, adminOnly: true },
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/AdminPaymentSettings.vue"
      ),
  },
  {
    path: "/admin/bids",
    name: "AdminBids",
    beforeEnter: guardAdmin,
    meta: { requiresAdmin: true },
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/AdminBids.vue"
      ),
  },
  {
    path: "/admin/bids/:id",
    name: "AdminBidDetail",
    beforeEnter: guardAdmin,
    meta: { requiresAdmin: true },
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/AdminBidDetail.vue"
      ),
  },
  {
    path: "/admin/sales-team",
    name: "AdminSalesTeam",
    beforeEnter: guardAdmin,
    meta: { requiresAdmin: true, adminOnly: true },
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/AdminSalesTeam.vue"
      ),
  },
  // Call center pages: Calls is in the admin sidebar; phone settings is reached from it.
  {
    path: "/admin/calls",
    name: "AdminCalls",
    beforeEnter: guardAdmin,
    meta: { requiresAdmin: true },
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/AdminCalls.vue"
      ),
  },
  {
    path: "/admin/phone-extensions",
    name: "AdminPhoneExtensions",
    beforeEnter: guardAdmin,
    meta: { requiresAdmin: true, adminOnly: true },
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/AdminPhoneExtensions.vue"
      ),
  },
  {
    path: "/admin/bid-reminders",
    name: "AdminBidReminders",
    beforeEnter: guardAdmin,
    meta: { requiresAdmin: true },
    component: () =>
      import(
        /* webpackChunkName: "admin" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/AdminBidReminders.vue"
      ),
  },
  {
    path: "/404",
    beforeEnter: guardMyroute,
    component: () =>
      import(
        /* webpackChunkName: "contact" */ "../views/" +
          process.env.VUE_APP_CURRENT_THEME +
          "/NotFound.vue"
      ),
  },
  { path: "/:catchAll(.*)", redirect: "/404" },
];

const router = createRouter({
  mode: "history",
  history: createWebHistory(process.env.VUE_APP_BUILD_PATH),
  routes,
});

export default router;
